import { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';

// own functions
import * as ownFunctions from './ContractConceptSelector.Functions';
import { Field, Label, Tag, TagPicker, TagPickerControl, TagPickerGroup, TagPickerInput, TagPickerList, TagPickerOption, TagPickerProps } from '@fluentui/react-components';

const areStringArraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) return false;
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) return false;
  }
  return true;
};


const ContractConceptSelectorComponent = (props: ownFunctions.Props) => {
  const { t } = useTranslation();

  useEffect(() => {
      props.GetContractById(props.ContractId);
  }, [props.ContractId]);

  useEffect(() => {
      if (props.Contract?.id) {
          props.GetAllContractsConcept(props.Contract.id, props.referralDate);
      }
  }, [props.Contract?.id, props.referralDate]);
  
  const [internalContractsSelected, setInternalContractsSelected] = useState<ContractConcept[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<ContractConcept[]>([]);
  const [selectedOptionValues, setSelectedOptionValues] = useState<string[]>([]);
  const requestedMissingConceptIdsRef = useRef<Set<string>>(new Set());

  const selectedOptionsRef = useRef(selectedOptions);
  const selectedOptionValuesRef = useRef(selectedOptionValues);
  selectedOptionsRef.current = selectedOptions;
  selectedOptionValuesRef.current = selectedOptionValues;

  useEffect(() => {
    if (!props.ContractConceptsSelected || props.ContractConceptsSelected.length === 0) {
      setInternalContractsSelected([]);
      setSelectedOptions([]);
      setSelectedOptionValues([]);
      return;
    }

    const selectedValues: string[] = [];
    const selectedConcepts: ContractConcept[] = [];
    props.ContractConceptsSelected.forEach(selected => {
      if (typeof selected === 'number' || typeof selected === 'string') {
        selectedValues.push(selected.toString());
        const concept = props.ContractConcepts.find(c => c.id.toString() === selected.toString());
        if (concept) {
          selectedConcepts.push(concept);
        }
      } else if (typeof selected === 'object') {
        const selectedConcept = selected as ContractConcept;
        selectedValues.push(selectedConcept.id.toString());
        selectedConcepts.push(selectedConcept);
      }
    });

    const nextSelectedConceptIds = selectedConcepts.map((concept) => concept.id.toString());
    const currentSelectedConceptIds = selectedOptionsRef.current.map((concept) => concept.id.toString());

    if (!areStringArraysEqual(selectedOptionValuesRef.current, selectedValues)) {
      setSelectedOptionValues(selectedValues);
    }

    if (!areStringArraysEqual(currentSelectedConceptIds, nextSelectedConceptIds)) {
      setInternalContractsSelected(selectedConcepts);
      setSelectedOptions(selectedConcepts);
    }
  }, [props.ContractConceptsSelected, props.ContractConcepts]);

  useEffect(() => {
    if (!selectedOptionValues.length) return;

    selectedOptionValues.forEach((value) => {
      const conceptExists = props.ContractConcepts.some((concept) => concept.id.toString() === value);
      if (conceptExists) {
        requestedMissingConceptIdsRef.current.delete(value);
      } else if (!requestedMissingConceptIdsRef.current.has(value)) {
        requestedMissingConceptIdsRef.current.add(value);
        const parsedId = Number(value);
        if (!Number.isNaN(parsedId)) {
          props.GetContractConceptById(parsedId);
        }
      }
    });
  }, [selectedOptionValues, props.ContractConcepts]);

  const tagPickerOptions = props.MultiselectEnable
    ? props.ContractConcepts?.filter((concept) => !selectedOptionValues.includes(concept.id.toString()))
    : props.ContractConcepts;

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (event, data) => {
      let selectedValues: string[] = [];
      if (props.MultiselectEnable) {
        selectedValues = data.selectedOptions;
      } else if (data.value) {
        selectedValues = [data.value];
      }

      const newSelectedOptions = selectedValues
        .map((value) => props.ContractConcepts.find(c => c.id.toString() === value))
        .filter((concept): concept is ContractConcept => concept !== undefined);

      setSelectedOptionValues(selectedValues);
      setInternalContractsSelected(newSelectedOptions);
      setSelectedOptions(newSelectedOptions);
      props.onSelectedContractConcept(newSelectedOptions);
  }

  return (<>
      <Field label={t('ContractConceptSelector.label', 'Select a concept')} style={{ maxWidth: 400 }}>
        <TagPicker
          onOptionSelect={onOptionSelect}
          selectedOptions={selectedOptionValues}
        >
          <TagPickerControl>
            <TagPickerGroup aria-label={t('ContractConceptSelector.selectedAria', 'Selected concepts')}>
              {selectedOptionValues.map((optionValue) => {
                const selectedConcept = props.ContractConcepts.find((option) => option.id.toString() === optionValue);
                return (
                <Tag
                  key={optionValue}
                  shape="rounded"
                  value={optionValue}
                >
                  {selectedConcept?.concept?.conceptName || optionValue}
                </Tag>
                );
              })}
            </TagPickerGroup>
            <TagPickerInput aria-label={t('ContractConceptSelector.inputAria', 'Select a concept')} />
          </TagPickerControl>
          <TagPickerList>
            {tagPickerOptions?.length > 0 ? (
              tagPickerOptions?.filter(option => option && option.concept).map((option) => (
                <TagPickerOption
                  value={option.id.toString()}
                  key={option.id}
                  text={option.concept.conceptName}
                >
                  {option.concept.conceptName}
                </TagPickerOption>
              ))
            ) : (<></>)}
          </TagPickerList>
        </TagPicker>
        {props.noteText && <Label>{props.noteText}</Label>}
      </Field>
  </>);
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(ContractConceptSelectorComponent as any);
